/**
 * PathwayInspector (PROD-591, extended in Sprint 4.3.7).
 *
 * Right-side panel showing the selected node's properties. Edits go
 * through this surface and chain into `usePathwayStore.editNode` /
 * `editLabBasketItem` which handle persistence + telemetry.
 *
 * Sprint 4.3.7 adds a `react-hook-form` + zod-backed save form for the
 * core node fields (label, description, category, trigger). The form
 * commits in three steps:
 *   1. Validate the form values against the zod schema.
 *   2. Apply the patch via `editNode` (in-memory + localStorage).
 *   3. If the active mode is `live` AND the tenant has a published
 *      PlanDefinition, write the new shape via
 *      `getFhirClient().updatePlanDefinition`. ETag conflicts surface as
 *      a user-friendly inline error.
 *
 * Render contract:
 *   - Empty state when `selectedNode` is null: "Select a node to view
 *     its properties."
 *   - Demographics / Vitals / Outcome: read-only label + description.
 *   - Labs: editable basket items (preselected, requiresConfirmation,
 *     rationale). TSH controls render disabled per HC8.
 *   - Decision Point: state machine + linked NBA ids + Override button
 *     that opens `<OverrideModal>` scoped to the pathway/step/nba.
 *   - NBA: title + description + Override button.
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §16:
 *   - Panel shell: Card (Sidebar) [GAP via G9]
 *   - Field rows: Component (Field-row) [GAP via G9]
 *
 * HC8 invariant: TSH preselect + requiresConfirmation toggles are
 * disabled. The store enforces this at the boundary; the UI mirrors
 * the invariant via `disabled` so the user gets immediate feedback.
 *
 * HC9 telemetry: every `editLabBasketItem` and `editNode` call emits
 * `pathway_edit`. The inspector does NOT emit directly; the store is
 * the single source of truth.
 *
 * HC5: zero em / en dashes.
 * HC6 a11y: every input has an associated `<label htmlFor>`. Selected
 * node region uses `aria-live="polite"` so SR users hear the new
 * inspector content when selection changes.
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ACTION_TYPES,
  FORM_QUESTION_TYPES,
  RULE_OPERATORS,
  RULE_RISK_TIERS,
  usePathwayStore,
  type ActionType,
  type EditablePathway,
  type FormQuestion,
  type FormQuestionType,
  type PathwayNode,
  type PathwayId,
  type PathwayRule,
  type RuleOperator,
  type RuleRiskTier,
} from '../../state/pathway.store';
import type { LabBasketItem } from '../../types/lab-basket';
import { OverrideModal } from '../OverrideModal';
import type { OverrideEvent } from '../../types/override';
import { useModeStore } from '../../state/mode.store';
import {
  EtagConflictError,
  getFhirClient,
  type FhirClient,
} from '../../services/fhir';
import { pathwayToPlanDefinition } from '../../services/fhir-mappers/pathway-to-plan-definition';
import { StakeholderPicker } from './StakeholderPicker';

export interface PathwayInspectorProps {
  pathway: EditablePathway;
  pathwayId: PathwayId;
  selectedNodeId: string | null;
  onOverrideSubmitted?: (event: OverrideEvent, nodeId: string) => void;
  /**
   * Sprint 4.3.7: optional FHIR client override for tests. Production
   * callers omit this and the inspector resolves the singleton.
   */
  fhirClient?: FhirClient;
  className?: string;
}

const EMPTY_STATE_MESSAGE = 'Select a node to view its properties.';
const TSH_HC8_HINT =
  'TSH stays preselected and requires confirmation per ADR-001 Layer 1. This row is read-only.';

/** Zod schema for the node-property form (Sprint 4.3.7). */
const nodeFormSchema = z.object({
  label: z.string().trim().min(1, 'Label is required.').max(200),
  description: z.string().max(1000).optional(),
  category: z.enum(['user', 'system']).optional(),
  triggerType: z.string().max(60).optional(),
  triggerFilter: z.string().max(120).optional(),
});
type NodeFormValues = z.infer<typeof nodeFormSchema>;

const ETAG_CONFLICT_MESSAGE =
  'Pathway was updated by another session. Refresh to see latest.';

export function PathwayInspector({
  pathway,
  pathwayId,
  selectedNodeId,
  onOverrideSubmitted,
  fhirClient,
  className,
}: PathwayInspectorProps) {
  const editNode = usePathwayStore((s) => s.editNode);
  const editLabBasketItem = usePathwayStore((s) => s.editLabBasketItem);
  const node =
    selectedNodeId === null
      ? null
      : pathway.nodes.find((n) => n.id === selectedNodeId) ?? null;

  const [overrideOpenForNodeId, setOverrideOpenForNodeId] = useState<
    string | null
  >(null);

  return (
    <aside
      aria-labelledby="pathway-inspector-title"
      data-testid="pathway-inspector"
      className={[
        'flex w-80 flex-col gap-gutter-md border-l border-surface-border bg-surface-muted p-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div>
        <h3
          id="pathway-inspector-title"
          className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Inspector
        </h3>
      </div>
      <div
        aria-live="polite"
        data-testid="pathway-inspector-body"
        className="flex flex-col gap-gutter-md"
      >
        {node === null ? (
          <p
            data-testid="pathway-inspector-empty-state"
            className="text-signal-sm text-text-tertiary"
          >
            {EMPTY_STATE_MESSAGE}
          </p>
        ) : (
          <>
            <NodePropertyForm
              key={node.id}
              pathway={pathway}
              pathwayId={pathwayId}
              node={node}
              fhirClient={fhirClient}
            />
            {node.nodeType === 'action' ? (
              <ActionTypePicker
                pathway={pathway}
                pathwayId={pathwayId}
                node={node}
              />
            ) : null}
            {node.nodeType === 'action' ? (
              <StakeholderAssignmentSection
                pathway={pathway}
                pathwayId={pathwayId}
                node={node}
              />
            ) : null}
            {node.nodeType === 'action' && node.actionType === 'form' ? (
              <FormQuestionsEditor
                pathway={pathway}
                pathwayId={pathwayId}
                node={node}
              />
            ) : null}
            {node.nodeType === 'step' ? (
              <TransitionRuleEditor
                pathway={pathway}
                pathwayId={pathwayId}
                node={node}
              />
            ) : null}
            <NodeFields
              pathway={pathway}
              pathwayId={pathwayId}
              node={node}
              onEditNode={(patch) =>
                editNode(pathway.tenantId, pathwayId, node.id, patch)
              }
              onEditLabBasketItem={(code, patch) =>
                editLabBasketItem(
                  pathway.tenantId,
                  pathwayId,
                  node.id,
                  code,
                  patch,
                )
              }
              onRequestOverride={() => setOverrideOpenForNodeId(node.id)}
            />
          </>
        )}
      </div>
      {node !== null ? (() => {
        const resolvedNbaId =
          node.nodeType === 'nba'
            ? node.id
            : node.linkedNbaIds && node.linkedNbaIds.length > 0
              ? node.linkedNbaIds[0]
              : null;
        if (resolvedNbaId === null) return null;
        return (
          <OverrideModal
            open={overrideOpenForNodeId === node.id}
            onClose={() => setOverrideOpenForNodeId(null)}
            pathwayId={pathway.id}
            stepId={node.id}
            nbaId={resolvedNbaId}
            nbaTitle={node.label}
            onSubmitted={(event) => {
              setOverrideOpenForNodeId(null);
              onOverrideSubmitted?.(event, node.id);
            }}
          />
        );
      })() : null}
    </aside>
  );
}

/**
 * Sprint 4.3.7: dedicated react-hook-form + zod surface for the node
 * core properties. Wraps `editNode` and (when applicable) the live
 * FHIR round-trip.
 */
interface NodePropertyFormProps {
  pathway: EditablePathway;
  pathwayId: PathwayId;
  node: PathwayNode;
  fhirClient?: FhirClient;
}

function NodePropertyForm({
  pathway,
  pathwayId,
  node,
  fhirClient,
}: NodePropertyFormProps) {
  const editNode = usePathwayStore((s) => s.editNode);
  const publishedByKey = usePathwayStore((s) => s.publishedByKey);
  const mode = useModeStore((s) => s.mode);
  const labelId = useId();
  const descriptionId = useId();
  const categoryId = useId();
  const triggerTypeId = useId();
  const triggerFilterId = useId();
  const [feedback, setFeedback] = useState<{
    kind: 'idle' | 'saved' | 'error';
    message: string;
  }>({ kind: 'idle', message: '' });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<NodeFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      label: node.label,
      description: node.description ?? '',
      category: node.category,
      triggerType: node.triggerType ?? '',
      triggerFilter: node.triggerFilter ?? '',
    },
  });

  // Reset the form whenever the selected node changes so a stale draft
  // does not bleed across selections.
  useEffect(() => {
    reset({
      label: node.label,
      description: node.description ?? '',
      category: node.category,
      triggerType: node.triggerType ?? '',
      triggerFilter: node.triggerFilter ?? '',
    });
    setFeedback({ kind: 'idle', message: '' });
  }, [node.id, node.label, node.description, node.category, node.triggerType, node.triggerFilter, reset]);

  const onSubmit = handleSubmit(async (raw) => {
    const parsed = nodeFormSchema.safeParse(raw);
    if (!parsed.success) {
      setFeedback({
        kind: 'error',
        message: parsed.error.errors[0]?.message ?? 'Invalid input.',
      });
      return;
    }
    const v = parsed.data;
    const patch = {
      label: v.label,
      description: v.description ?? '',
      ...(node.nodeType === 'action' && v.category
        ? { category: v.category }
        : {}),
      ...(node.nodeType === 'track'
        ? {
            triggerType: v.triggerType ?? '',
            triggerFilter: v.triggerFilter ?? '',
          }
        : {}),
    };
    const updated = editNode(pathway.tenantId, pathwayId, node.id, patch);
    if (updated === null) {
      setFeedback({ kind: 'error', message: 'Edit could not be persisted.' });
      return;
    }
    // Live mode FHIR round-trip. We attempt only when a published
    // resource is already cached for this pathway (i.e. the tenant has
    // published at least once). First-publish goes through the
    // dedicated PathwayPublishFlow.
    const cacheKey = `${pathway.tenantId}::${pathwayId}`;
    const cached = publishedByKey[cacheKey];
    if (mode === 'live' && cached?.versionId) {
      const client = fhirClient ?? getFhirClient();
      try {
        const planDef = pathwayToPlanDefinition(updated);
        await client.updatePlanDefinition(
          planDef,
          pathway.tenantId,
          cached.versionId,
        );
      } catch (err) {
        if (err instanceof EtagConflictError) {
          setFeedback({ kind: 'error', message: ETAG_CONFLICT_MESSAGE });
          return;
        }
        const m = err instanceof Error ? err.message : 'Unknown error.';
        setFeedback({ kind: 'error', message: `Live save failed. ${m}` });
        return;
      }
    }
    setFeedback({ kind: 'saved', message: 'Saved.' });
    reset({
      label: v.label,
      description: v.description ?? '',
      category: v.category,
      triggerType: v.triggerType ?? '',
      triggerFilter: v.triggerFilter ?? '',
    });
  });

  return (
    <form
      data-testid={`pathway-inspector-form-${node.id}`}
      onSubmit={onSubmit}
      className="flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm"
    >
      <p className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
        Node properties
      </p>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={labelId}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Label
        </label>
        <input
          id={labelId}
          type="text"
          data-testid={`pathway-inspector-form-label-${node.id}`}
          {...register('label', { required: true })}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        />
        {errors.label ? (
          <span
            role="alert"
            data-testid={`pathway-inspector-form-error-label-${node.id}`}
            className="text-signal-xs text-signal-danger-500"
          >
            {errors.label.message ?? 'Label is required.'}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={descriptionId}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Description
        </label>
        <textarea
          id={descriptionId}
          rows={2}
          data-testid={`pathway-inspector-form-description-${node.id}`}
          {...register('description')}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        />
      </div>
      {node.nodeType === 'action' ? (
        <div className="flex flex-col gap-1">
          <label
            htmlFor={categoryId}
            className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
          >
            Category
          </label>
          <select
            id={categoryId}
            data-testid={`pathway-inspector-form-category-${node.id}`}
            {...register('category')}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            <option value="user">User</option>
            <option value="system">System</option>
          </select>
        </div>
      ) : null}
      {node.nodeType === 'track' ? (
        <>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={triggerTypeId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Trigger type
            </label>
            <input
              id={triggerTypeId}
              type="text"
              data-testid={`pathway-inspector-form-trigger-type-${node.id}`}
              {...register('triggerType')}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={triggerFilterId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Trigger filter
            </label>
            <input
              id={triggerFilterId}
              type="text"
              data-testid={`pathway-inspector-form-trigger-filter-${node.id}`}
              {...register('triggerFilter')}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </div>
        </>
      ) : null}
      {node.parentNodeId ? (
        <p
          className="text-signal-xs text-text-tertiary"
          data-testid={`pathway-inspector-form-parent-${node.id}`}
        >
          Parent: <span className="font-mono">{node.parentNodeId}</span>
        </p>
      ) : null}
      <div className="flex items-center justify-between">
        <button
          type="submit"
          data-testid={`pathway-inspector-form-save-${node.id}`}
          disabled={isSubmitting || !isDirty}
          className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-600 focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-60"
        >
          Save
        </button>
        {feedback.kind !== 'idle' ? (
          <span
            role={feedback.kind === 'error' ? 'alert' : 'status'}
            data-testid={`pathway-inspector-form-feedback-${node.id}`}
            data-feedback-kind={feedback.kind}
            className={[
              'text-signal-xs',
              feedback.kind === 'error'
                ? 'text-signal-danger-500'
                : 'text-signal-success-500',
            ].join(' ')}
          >
            {feedback.message}
          </span>
        ) : null}
      </div>
    </form>
  );
}

interface NodeFieldsProps {
  pathway: EditablePathway;
  pathwayId: PathwayId;
  node: PathwayNode;
  onEditNode: (patch: { label?: string; stateMachine?: string; description?: string; requiresConfirmation?: boolean }) => void;
  onEditLabBasketItem: (
    code: string,
    patch: Partial<Pick<LabBasketItem, 'preselected' | 'requiresConfirmation' | 'rationale'>>,
  ) => void;
  onRequestOverride: () => void;
}

function NodeFields({
  pathway,
  pathwayId,
  node,
  onEditNode,
  onEditLabBasketItem,
  onRequestOverride,
}: NodeFieldsProps) {
  return (
    <div
      data-testid={`pathway-inspector-node-${node.id}`}
      data-node-type={node.nodeType}
      data-pathway-id={pathway.id}
      data-pathway={pathwayId}
      className="flex flex-col gap-gutter-md"
    >
      <NodeHeader node={node} onEditNode={onEditNode} />
      {node.nodeType === 'labs' && node.labBasketItems ? (
        <LabBasketEditor
          nodeId={node.id}
          items={node.labBasketItems}
          onEditItem={onEditLabBasketItem}
        />
      ) : null}
      {node.nodeType === 'decision' ? (
        <DecisionPointFields
          node={node}
          onEditNode={onEditNode}
          onRequestOverride={onRequestOverride}
        />
      ) : null}
      {node.nodeType === 'nba' ? (
        <button
          type="button"
          data-testid={`pathway-inspector-override-${node.id}`}
          onClick={onRequestOverride}
          className="self-start rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          Open override modal
        </button>
      ) : null}
    </div>
  );
}

interface NodeHeaderProps {
  node: PathwayNode;
  onEditNode: NodeFieldsProps['onEditNode'];
}

function NodeHeader({ node, onEditNode }: NodeHeaderProps) {
  const labelId = useId();
  const descriptionId = useId();
  return (
    <div className="flex flex-col gap-gutter-sm">
      <div className="flex flex-col gap-1">
        <label
          htmlFor={labelId}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Label
        </label>
        <input
          id={labelId}
          type="text"
          data-testid={`pathway-inspector-label-${node.id}`}
          defaultValue={node.label}
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next.length > 0 && next !== node.label) {
              onEditNode({ label: next });
            }
          }}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={descriptionId}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Description
        </label>
        <textarea
          id={descriptionId}
          data-testid={`pathway-inspector-description-${node.id}`}
          defaultValue={node.description ?? ''}
          rows={3}
          onBlur={(e) => {
            const next = e.target.value;
            if (next !== (node.description ?? '')) {
              onEditNode({ description: next });
            }
          }}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        />
      </div>
      <p className="text-signal-xs text-text-tertiary">
        Type: <span className="font-semibold">{node.nodeType}</span>
      </p>
    </div>
  );
}

interface LabBasketEditorProps {
  nodeId: string;
  items: LabBasketItem[];
  onEditItem: NodeFieldsProps['onEditLabBasketItem'];
}

function LabBasketEditor({ nodeId, items, onEditItem }: LabBasketEditorProps) {
  return (
    <fieldset
      data-testid={`pathway-inspector-lab-basket-${nodeId}`}
      className="flex flex-col gap-gutter-sm"
    >
      <legend className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
        Lab basket
      </legend>
      <ul className="flex flex-col gap-gutter-sm">
        {items.map((item) => {
          const isTsh = item.code === 'TSH';
          const preselectedId = `lab-${nodeId}-${item.code}-preselected`;
          const requiresConfirmationId = `lab-${nodeId}-${item.code}-requires-confirmation`;
          return (
            <li
              key={item.code}
              data-testid={`pathway-inspector-lab-item-${item.code}`}
              data-tsh-readonly={isTsh ? 'true' : 'false'}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-gutter-sm"
            >
              <div className="flex items-baseline justify-between gap-gutter-sm">
                <span className="text-signal-sm font-semibold text-text-primary">
                  {item.name} ({item.code})
                </span>
                {isTsh ? (
                  <span
                    title={TSH_HC8_HINT}
                    className="rounded-signal-pill bg-warning-soft-bg px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wide text-warning-soft-fg"
                  >
                    HC8 read-only
                  </span>
                ) : null}
              </div>
              <div className="mt-1 flex flex-col gap-1">
                <label
                  htmlFor={preselectedId}
                  className="flex items-center gap-1 text-signal-xs text-text-secondary"
                >
                  <input
                    id={preselectedId}
                    type="checkbox"
                    data-testid={`pathway-inspector-lab-${item.code}-preselected`}
                    disabled={isTsh}
                    checked={item.preselected}
                    onChange={(e) =>
                      onEditItem(item.code, { preselected: e.target.checked })
                    }
                  />
                  Preselected
                </label>
                <label
                  htmlFor={requiresConfirmationId}
                  className="flex items-center gap-1 text-signal-xs text-text-secondary"
                >
                  <input
                    id={requiresConfirmationId}
                    type="checkbox"
                    data-testid={`pathway-inspector-lab-${item.code}-requires-confirmation`}
                    disabled={isTsh}
                    checked={item.requiresConfirmation}
                    onChange={(e) =>
                      onEditItem(item.code, {
                        requiresConfirmation: e.target.checked,
                      })
                    }
                  />
                  Requires confirmation
                </label>
              </div>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

interface DecisionPointFieldsProps {
  node: PathwayNode;
  onEditNode: NodeFieldsProps['onEditNode'];
  onRequestOverride: () => void;
}

function DecisionPointFields({
  node,
  onEditNode,
  onRequestOverride,
}: DecisionPointFieldsProps) {
  const stateMachineId = useId();
  return (
    <div
      data-testid={`pathway-inspector-decision-${node.id}`}
      className="flex flex-col gap-gutter-sm"
    >
      <div className="flex flex-col gap-1">
        <label
          htmlFor={stateMachineId}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          State machine
        </label>
        <textarea
          id={stateMachineId}
          data-testid={`pathway-inspector-state-machine-${node.id}`}
          defaultValue={node.stateMachine ?? ''}
          rows={3}
          onBlur={(e) => {
            const next = e.target.value;
            if (next !== (node.stateMachine ?? '')) {
              onEditNode({ stateMachine: next });
            }
          }}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Linked NBA candidates
        </span>
        <ul
          data-testid={`pathway-inspector-linked-nbas-${node.id}`}
          className="flex flex-col gap-1"
        >
          {(node.linkedNbaIds ?? []).map((id) => (
            <li
              key={id}
              data-testid={`pathway-inspector-linked-nba-${id}`}
              className="rounded-signal-pill bg-surface-base px-gutter-sm py-0.5 text-signal-xs text-text-secondary"
            >
              {id}
            </li>
          ))}
        </ul>
      </div>
      <button
        type="button"
        data-testid={`pathway-inspector-override-${node.id}`}
        onClick={onRequestOverride}
        className="self-start rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        Open override modal
      </button>
    </div>
  );
}

/**
 * Sprint 4.3.8: stakeholder assignment surface for Action nodes.
 *
 * Wraps the StakeholderPicker with the pathway-store edit binding so a
 * picker selection commits to the node's `assignedStakeholderId` field
 * and lands in the pathway-edit telemetry stream. The set of "other
 * assignees" is derived from the same pathway's other action nodes so
 * the picker can surface separation-of-duty warnings per Phase B
 * `stakeholder-model.md`.
 */
interface StakeholderAssignmentSectionProps {
  pathway: EditablePathway;
  pathwayId: PathwayId;
  node: PathwayNode;
}

function StakeholderAssignmentSection({
  pathway,
  pathwayId,
  node,
}: StakeholderAssignmentSectionProps) {
  const editNode = usePathwayStore((s) => s.editNode);
  const otherAssignedStakeholderIds = pathway.nodes
    .filter(
      (n) =>
        n.id !== node.id &&
        n.nodeType === 'action' &&
        typeof n.assignedStakeholderId === 'string' &&
        n.assignedStakeholderId.length > 0,
    )
    .map((n) => n.assignedStakeholderId as string);
  return (
    <div
      data-testid={`pathway-inspector-stakeholder-${node.id}`}
      className="flex flex-col gap-1"
    >
      <StakeholderPicker
        selectedStakeholderId={node.assignedStakeholderId ?? null}
        otherAssignedStakeholderIds={otherAssignedStakeholderIds}
        onSelect={(stakeholderId) => {
          editNode(pathway.tenantId, pathwayId, node.id, {
            assignedStakeholderId: stakeholderId,
          });
        }}
        onClear={() => {
          editNode(pathway.tenantId, pathwayId, node.id, {
            assignedStakeholderId: '',
          });
        }}
      />
    </div>
  );
}

/**
 * Sprint 4.3.9: native action-type selector. Toggles the discriminator
 * `actionType` on an Action node. When the user picks `form`, the
 * inspector mounts the `FormQuestionsEditor` so the user can author
 * questions against the same node. The selector is keyboard-friendly
 * (real `<select>` element) and labelled per HC6.
 */
interface ActionTypePickerProps {
  pathway: EditablePathway;
  pathwayId: PathwayId;
  node: PathwayNode;
}

const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  form: 'Form',
  calculation: 'Calculation',
  message: 'Message',
  api_call: 'API call',
  reminder: 'Reminder',
  checklist: 'Checklist',
};

function ActionTypePicker({
  pathway,
  pathwayId,
  node,
}: ActionTypePickerProps) {
  const editNode = usePathwayStore((s) => s.editNode);
  const selectId = useId();
  const value = node.actionType ?? '';
  return (
    <div
      data-testid={`pathway-inspector-action-type-${node.id}`}
      className="flex flex-col gap-1"
    >
      <label
        htmlFor={selectId}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        Action type
      </label>
      <select
        id={selectId}
        data-testid={`pathway-inspector-action-type-select-${node.id}`}
        aria-label="Action type"
        value={value}
        onChange={(e) => {
          const next = e.target.value as ActionType | '';
          if (next === '') {
            editNode(pathway.tenantId, pathwayId, node.id, {
              actionType: undefined,
            });
            return;
          }
          editNode(pathway.tenantId, pathwayId, node.id, {
            actionType: next,
          });
        }}
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        <option value="">Choose action type</option>
        {ACTION_TYPES.map((t) => (
          <option key={t} value={t}>
            {ACTION_TYPE_LABELS[t]}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Sprint 4.3.9: form questions authoring sub-panel.
 *
 * Renders an ordered, editable list of questions for a form-type Action
 * node. Each row supports:
 *   - Edit text / type / required / data-point-definition binding.
 *   - Remove the question.
 *   - Move up / move down (HC6 keyboard-accessible reorder).
 *   - Per-row option editor for choice types.
 *
 * Edits stage in local state and commit to the pathway store on Save so
 * the user can author multi-question batches without round-tripping
 * through the FHIR client per keystroke. Save commits the full
 * `formQuestions` list via `editNode`; the store treats the patch as a
 * list-replace.
 */
interface FormQuestionsEditorProps {
  pathway: EditablePathway;
  pathwayId: PathwayId;
  node: PathwayNode;
}

const FORM_QUESTION_TYPE_LABELS: Record<FormQuestionType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  scale: 'Scale',
};

let formQuestionIdCounter = 0;
function freshFormQuestionId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as { randomUUID?: () => string }).randomUUID === 'function'
  ) {
    return `q-${(crypto as { randomUUID: () => string }).randomUUID()}`;
  }
  formQuestionIdCounter += 1;
  return `q-${formQuestionIdCounter}`;
}

function FormQuestionsEditor({
  pathway,
  pathwayId,
  node,
}: FormQuestionsEditorProps) {
  const editNode = usePathwayStore((s) => s.editNode);
  const [draft, setDraft] = useState<FormQuestion[]>(
    () => node.formQuestions?.map((q) => ({ ...q, options: q.options?.map((o) => ({ ...o })) })) ?? [],
  );
  const [feedback, setFeedback] = useState<{
    kind: 'idle' | 'saved' | 'error';
    message: string;
  }>({ kind: 'idle', message: '' });

  // Reset the draft when the selected node changes. We intentionally
  // do NOT depend on `node.formQuestions` here so a save round-trip
  // (which re-references the array) does not wipe the just-set
  // feedback message.
  useEffect(() => {
    setDraft(
      node.formQuestions?.map((q) => ({
        ...q,
        options: q.options?.map((o) => ({ ...o })),
      })) ?? [],
    );
    setFeedback({ kind: 'idle', message: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const updateQuestion = useCallback(
    (idx: number, patch: Partial<FormQuestion>) => {
      setDraft((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], ...patch };
        return next;
      });
    },
    [],
  );

  const addQuestion = useCallback(() => {
    setDraft((prev) => [
      ...prev,
      {
        id: freshFormQuestionId(),
        text: '',
        type: 'text',
        required: false,
      },
    ]);
  }, []);

  const removeQuestion = useCallback((idx: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const moveQuestion = useCallback((idx: number, dir: 'up' | 'down') => {
    setDraft((prev) => {
      const target = dir === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const tmp = next[idx];
      next[idx] = next[target];
      next[target] = tmp;
      return next;
    });
  }, []);

  const addOption = useCallback((idx: number) => {
    setDraft((prev) => {
      const next = [...prev];
      const opts = [...(next[idx].options ?? [])];
      opts.push({ value: `opt-${opts.length + 1}`, label: '' });
      next[idx] = { ...next[idx], options: opts };
      return next;
    });
  }, []);

  const updateOption = useCallback(
    (
      qIdx: number,
      oIdx: number,
      patch: Partial<{ value: string; label: string }>,
    ) => {
      setDraft((prev) => {
        const next = [...prev];
        const opts = [...(next[qIdx].options ?? [])];
        opts[oIdx] = { ...opts[oIdx], ...patch };
        next[qIdx] = { ...next[qIdx], options: opts };
        return next;
      });
    },
    [],
  );

  const removeOption = useCallback((qIdx: number, oIdx: number) => {
    setDraft((prev) => {
      const next = [...prev];
      const opts = (next[qIdx].options ?? []).filter((_, i) => i !== oIdx);
      next[qIdx] = { ...next[qIdx], options: opts };
      return next;
    });
  }, []);

  const onSave = useCallback(() => {
    // Validate each question has a non-empty text label and each choice
    // type has at least one option.
    for (let i = 0; i < draft.length; i += 1) {
      const q = draft[i];
      if (q.text.trim().length === 0) {
        setFeedback({
          kind: 'error',
          message: `Question ${i + 1} needs a label.`,
        });
        return;
      }
      if (
        (q.type === 'single_choice' || q.type === 'multiple_choice') &&
        (!q.options || q.options.length === 0)
      ) {
        setFeedback({
          kind: 'error',
          message: `Question ${i + 1} needs at least one option.`,
        });
        return;
      }
    }
    const updated = editNode(pathway.tenantId, pathwayId, node.id, {
      formQuestions: draft.map((q) => ({
        ...q,
        options: q.options?.map((o) => ({ ...o })),
      })),
    });
    if (updated === null) {
      setFeedback({ kind: 'error', message: 'Save failed.' });
      return;
    }
    setFeedback({ kind: 'saved', message: 'Questions saved.' });
  }, [draft, editNode, node.id, pathway.tenantId, pathwayId]);

  return (
    <section
      data-testid={`pathway-inspector-form-questions-${node.id}`}
      aria-labelledby={`pathway-inspector-form-questions-title-${node.id}`}
      className="flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm"
    >
      <header className="flex items-baseline justify-between gap-gutter-sm">
        <h4
          id={`pathway-inspector-form-questions-title-${node.id}`}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Questions
        </h4>
        <button
          type="button"
          data-testid={`pathway-inspector-form-questions-add-${node.id}`}
          onClick={addQuestion}
          aria-label="Add question"
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          Add question
        </button>
      </header>
      {draft.length === 0 ? (
        <p
          data-testid={`pathway-inspector-form-questions-empty-${node.id}`}
          className="text-signal-xs text-text-tertiary"
        >
          No questions yet. Click Add question to start.
        </p>
      ) : (
        <ol
          data-testid={`pathway-inspector-form-questions-list-${node.id}`}
          className="flex flex-col gap-gutter-sm"
        >
          {draft.map((q, idx) => (
            <FormQuestionRow
              key={q.id}
              nodeId={node.id}
              question={q}
              index={idx}
              total={draft.length}
              onUpdate={(patch) => updateQuestion(idx, patch)}
              onRemove={() => removeQuestion(idx)}
              onMoveUp={() => moveQuestion(idx, 'up')}
              onMoveDown={() => moveQuestion(idx, 'down')}
              onAddOption={() => addOption(idx)}
              onUpdateOption={(oIdx, patch) => updateOption(idx, oIdx, patch)}
              onRemoveOption={(oIdx) => removeOption(idx, oIdx)}
            />
          ))}
        </ol>
      )}
      <div className="flex items-center justify-between gap-gutter-sm">
        <button
          type="button"
          data-testid={`pathway-inspector-form-questions-save-${node.id}`}
          onClick={onSave}
          className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-600 focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          Save questions
        </button>
        {feedback.kind !== 'idle' ? (
          <span
            role={feedback.kind === 'error' ? 'alert' : 'status'}
            data-testid={`pathway-inspector-form-questions-feedback-${node.id}`}
            data-feedback-kind={feedback.kind}
            className={[
              'text-signal-xs',
              feedback.kind === 'error'
                ? 'text-signal-danger-500'
                : 'text-signal-success-500',
            ].join(' ')}
          >
            {feedback.message}
          </span>
        ) : null}
      </div>
    </section>
  );
}

interface FormQuestionRowProps {
  nodeId: string;
  question: FormQuestion;
  index: number;
  total: number;
  onUpdate: (patch: Partial<FormQuestion>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddOption: () => void;
  onUpdateOption: (
    oIdx: number,
    patch: Partial<{ value: string; label: string }>,
  ) => void;
  onRemoveOption: (oIdx: number) => void;
}

function FormQuestionRow({
  nodeId,
  question,
  index,
  total,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
}: FormQuestionRowProps) {
  const textId = useId();
  const typeId = useId();
  const requiredId = useId();
  const dpdId = useId();
  const isChoice =
    question.type === 'single_choice' || question.type === 'multiple_choice';
  const isScale = question.type === 'scale';
  return (
    <li
      data-testid={`pathway-inspector-form-question-${nodeId}-${question.id}`}
      data-question-index={index}
      className="flex flex-col gap-1 rounded-signal-md border border-surface-border bg-surface-muted p-gutter-sm"
    >
      <div className="flex items-baseline justify-between gap-gutter-sm">
        <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Question {index + 1}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`pathway-inspector-form-question-up-${nodeId}-${question.id}`}
            aria-label={`Move question ${index + 1} up`}
            disabled={index === 0}
            onClick={onMoveUp}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-50"
          >
            Up
          </button>
          <button
            type="button"
            data-testid={`pathway-inspector-form-question-down-${nodeId}-${question.id}`}
            aria-label={`Move question ${index + 1} down`}
            disabled={index === total - 1}
            onClick={onMoveDown}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-50"
          >
            Down
          </button>
          <button
            type="button"
            data-testid={`pathway-inspector-form-question-remove-${nodeId}-${question.id}`}
            aria-label={`Remove question ${index + 1}`}
            onClick={onRemove}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            Remove
          </button>
        </div>
      </div>
      <label htmlFor={textId} className="text-signal-xs text-text-tertiary">
        Question text
      </label>
      <input
        id={textId}
        type="text"
        data-testid={`pathway-inspector-form-question-text-${nodeId}-${question.id}`}
        aria-label={`Question ${index + 1} text`}
        value={question.text}
        onChange={(e) => onUpdate({ text: e.target.value })}
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      />
      <label htmlFor={typeId} className="text-signal-xs text-text-tertiary">
        Type
      </label>
      <select
        id={typeId}
        data-testid={`pathway-inspector-form-question-type-${nodeId}-${question.id}`}
        aria-label={`Question ${index + 1} type`}
        value={question.type}
        onChange={(e) => {
          const next = e.target.value as FormQuestionType;
          const patch: Partial<FormQuestion> = { type: next };
          // Reset choice options when leaving choice types so the saved
          // shape never carries stale options for a non-choice question.
          if (next !== 'single_choice' && next !== 'multiple_choice') {
            patch.options = undefined;
          }
          if (next === 'scale') {
            patch.scaleMin = question.scaleMin ?? 0;
            patch.scaleMax = question.scaleMax ?? 10;
          } else {
            patch.scaleMin = undefined;
            patch.scaleMax = undefined;
          }
          onUpdate(patch);
        }}
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        {FORM_QUESTION_TYPES.map((t) => (
          <option key={t} value={t}>
            {FORM_QUESTION_TYPE_LABELS[t]}
          </option>
        ))}
      </select>
      <label
        htmlFor={requiredId}
        className="flex items-center gap-1 text-signal-xs text-text-secondary"
      >
        <input
          id={requiredId}
          type="checkbox"
          data-testid={`pathway-inspector-form-question-required-${nodeId}-${question.id}`}
          aria-label={`Question ${index + 1} required`}
          checked={question.required}
          onChange={(e) => onUpdate({ required: e.target.checked })}
        />
        Required
      </label>
      <label htmlFor={dpdId} className="text-signal-xs text-text-tertiary">
        Data point definition id (optional)
      </label>
      <input
        id={dpdId}
        type="text"
        data-testid={`pathway-inspector-form-question-dpd-${nodeId}-${question.id}`}
        aria-label={`Question ${index + 1} data point definition id`}
        value={question.dataPointDefinitionId ?? ''}
        onChange={(e) =>
          onUpdate({
            dataPointDefinitionId:
              e.target.value.trim().length > 0 ? e.target.value : undefined,
          })
        }
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      />
      {isChoice ? (
        <div
          data-testid={`pathway-inspector-form-question-options-${nodeId}-${question.id}`}
          className="flex flex-col gap-1 rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm"
        >
          <div className="flex items-baseline justify-between gap-gutter-sm">
            <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
              Options
            </span>
            <button
              type="button"
              data-testid={`pathway-inspector-form-question-option-add-${nodeId}-${question.id}`}
              onClick={onAddOption}
              aria-label={`Add option to question ${index + 1}`}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              Add option
            </button>
          </div>
          {(question.options ?? []).map((opt, oIdx) => (
            <div
              key={`${question.id}-opt-${oIdx}`}
              data-testid={`pathway-inspector-form-question-option-${nodeId}-${question.id}-${oIdx}`}
              className="flex items-center gap-1"
            >
              <input
                type="text"
                aria-label={`Option ${oIdx + 1} value`}
                data-testid={`pathway-inspector-form-question-option-value-${nodeId}-${question.id}-${oIdx}`}
                value={opt.value}
                onChange={(e) => onUpdateOption(oIdx, { value: e.target.value })}
                className="w-1/3 rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
                placeholder="value"
              />
              <input
                type="text"
                aria-label={`Option ${oIdx + 1} label`}
                data-testid={`pathway-inspector-form-question-option-label-${nodeId}-${question.id}-${oIdx}`}
                value={opt.label}
                onChange={(e) => onUpdateOption(oIdx, { label: e.target.value })}
                className="flex-1 rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
                placeholder="label"
              />
              <button
                type="button"
                aria-label={`Remove option ${oIdx + 1}`}
                data-testid={`pathway-inspector-form-question-option-remove-${nodeId}-${question.id}-${oIdx}`}
                onClick={() => onRemoveOption(oIdx)}
                className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {isScale ? (
        <div
          data-testid={`pathway-inspector-form-question-scale-${nodeId}-${question.id}`}
          className="flex items-center gap-gutter-sm"
        >
          <label className="flex items-center gap-1 text-signal-xs text-text-secondary">
            Min
            <input
              type="number"
              data-testid={`pathway-inspector-form-question-scale-min-${nodeId}-${question.id}`}
              aria-label={`Question ${index + 1} scale min`}
              value={question.scaleMin ?? 0}
              onChange={(e) =>
                onUpdate({ scaleMin: Number(e.target.value) })
              }
              className="w-16 rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </label>
          <label className="flex items-center gap-1 text-signal-xs text-text-secondary">
            Max
            <input
              type="number"
              data-testid={`pathway-inspector-form-question-scale-max-${nodeId}-${question.id}`}
              aria-label={`Question ${index + 1} scale max`}
              value={question.scaleMax ?? 10}
              onChange={(e) =>
                onUpdate({ scaleMax: Number(e.target.value) })
              }
              className="w-16 rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </label>
        </div>
      ) : null}
      <FormQuestionEnableWhenEditor
        nodeId={nodeId}
        question={question}
        onUpdate={onUpdate}
      />
    </li>
  );
}

/**
 * Sprint 4.3.10: rules editor surface.
 *
 * Two attachment points:
 *   1. Step node -> `TransitionRuleEditor` -> `node.transitionRule`
 *      (gates the step's outbound transition).
 *   2. Form-question row -> `FormQuestionEnableWhenEditor` -> question
 *      `enableWhenRule` (gates the question's display).
 *
 * Both editors share the same `<RuleControls>` sub-component so the
 * operator + operand + value + risk-tier controls render identically
 * in both surfaces. Per Sprint 4.3.10 scope: single-condition rules
 * only (no AND / OR composition); single-operand single-operator.
 *
 * HC2: Signal DS tokens only; styling mirrors the FormQuestionsEditor
 * shell so the inspector reads as one panel.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: every control carries a stable id + aria-label; the panel
 * exposes an "Add rule" / "Remove rule" affordance pair.
 */
const RULE_OPERATOR_LABELS: Record<RuleOperator, string> = {
  equals: 'equals',
  'not-equals': 'not equals',
  'greater-than': 'greater than',
  'less-than': 'less than',
  'greater-than-or-equals': 'greater than or equals',
  'less-than-or-equals': 'less than or equals',
  in: 'in (any of)',
  'not-in': 'not in',
  'is-empty': 'is empty',
  'is-not-empty': 'is not empty',
};

const RULE_RISK_TIER_LABELS: Record<RuleRiskTier, string> = {
  standard: 'Standard',
  elevated: 'Elevated (supervisor review)',
  critical: 'Critical (supervisor review)',
};

let ruleIdCounter = 0;
function freshRuleId(prefix: 'rule' | 'enable'): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as { randomUUID?: () => string }).randomUUID === 'function'
  ) {
    return `${prefix}-${(crypto as { randomUUID: () => string }).randomUUID()}`;
  }
  ruleIdCounter += 1;
  return `${prefix}-${ruleIdCounter}`;
}

function operatorTakesValue(operator: RuleOperator): boolean {
  return operator !== 'is-empty' && operator !== 'is-not-empty';
}

function operatorTakesArrayValue(operator: RuleOperator): boolean {
  return operator === 'in' || operator === 'not-in';
}

interface RuleControlsProps {
  testidPrefix: string;
  rule: PathwayRule;
  onChange: (next: PathwayRule) => void;
}

function RuleControls({ testidPrefix, rule, onChange }: RuleControlsProps) {
  const nameId = useId();
  const operandId = useId();
  const operatorId = useId();
  const valueId = useId();
  const riskId = useId();
  const valueAsString = (() => {
    if (rule.value === undefined) return '';
    if (Array.isArray(rule.value)) return rule.value.join(',');
    return String(rule.value);
  })();
  return (
    <div
      data-testid={`${testidPrefix}-controls`}
      className="flex flex-col gap-1"
    >
      <label
        htmlFor={nameId}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        Rule name
      </label>
      <input
        id={nameId}
        type="text"
        data-testid={`${testidPrefix}-name`}
        aria-label="Rule name"
        value={rule.name}
        onChange={(e) => onChange({ ...rule, name: e.target.value })}
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      />
      <label
        htmlFor={operandId}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        Data point definition id
      </label>
      <input
        id={operandId}
        type="text"
        data-testid={`${testidPrefix}-operand`}
        aria-label="Data point definition id"
        value={rule.dataPointDefinitionId}
        onChange={(e) =>
          onChange({ ...rule, dataPointDefinitionId: e.target.value })
        }
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      />
      <label
        htmlFor={operatorId}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        Operator
      </label>
      <select
        id={operatorId}
        data-testid={`${testidPrefix}-operator`}
        aria-label="Operator"
        value={rule.operator}
        onChange={(e) => {
          const nextOp = e.target.value as RuleOperator;
          const next: PathwayRule = { ...rule, operator: nextOp };
          if (!operatorTakesValue(nextOp)) {
            next.value = undefined;
          } else if (
            operatorTakesArrayValue(nextOp) &&
            !Array.isArray(rule.value)
          ) {
            next.value = [];
          } else if (
            !operatorTakesArrayValue(nextOp) &&
            Array.isArray(rule.value)
          ) {
            next.value = '';
          }
          onChange(next);
        }}
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        {RULE_OPERATORS.map((op) => (
          <option key={op} value={op}>
            {RULE_OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>
      {operatorTakesValue(rule.operator) ? (
        <>
          <label
            htmlFor={valueId}
            className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
          >
            {operatorTakesArrayValue(rule.operator)
              ? 'Values (comma separated)'
              : 'Value'}
          </label>
          <input
            id={valueId}
            type="text"
            data-testid={`${testidPrefix}-value`}
            aria-label="Rule value"
            value={valueAsString}
            onChange={(e) => {
              const raw = e.target.value;
              if (operatorTakesArrayValue(rule.operator)) {
                const arr = raw
                  .split(',')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0);
                onChange({ ...rule, value: arr });
                return;
              }
              const asNumber = Number(raw);
              if (
                raw.trim().length > 0 &&
                !Number.isNaN(asNumber) &&
                /^-?\d+(\.\d+)?$/.test(raw.trim())
              ) {
                onChange({ ...rule, value: asNumber });
                return;
              }
              onChange({ ...rule, value: raw });
            }}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          />
        </>
      ) : null}
      <label
        htmlFor={riskId}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        Risk tier
      </label>
      <select
        id={riskId}
        data-testid={`${testidPrefix}-risk`}
        aria-label="Risk tier"
        value={rule.riskTier ?? 'standard'}
        onChange={(e) => {
          const next = e.target.value as RuleRiskTier;
          if (next === 'standard') {
            const copy: PathwayRule = { ...rule };
            delete copy.riskTier;
            onChange(copy);
            return;
          }
          onChange({ ...rule, riskTier: next });
        }}
        className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        {RULE_RISK_TIERS.map((t) => (
          <option key={t} value={t}>
            {RULE_RISK_TIER_LABELS[t]}
          </option>
        ))}
      </select>
    </div>
  );
}

interface TransitionRuleEditorProps {
  pathway: EditablePathway;
  pathwayId: PathwayId;
  node: PathwayNode;
}

function TransitionRuleEditor({
  pathway,
  pathwayId,
  node,
}: TransitionRuleEditorProps) {
  const editNode = usePathwayStore((s) => s.editNode);
  const initial: PathwayRule | null = node.transitionRule
    ? { ...node.transitionRule }
    : null;
  const [draft, setDraft] = useState<PathwayRule | null>(initial);
  const [feedback, setFeedback] = useState<{
    kind: 'idle' | 'saved' | 'error';
    message: string;
  }>({ kind: 'idle', message: '' });

  useEffect(() => {
    setDraft(node.transitionRule ? { ...node.transitionRule } : null);
    setFeedback({ kind: 'idle', message: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const onAdd = useCallback(() => {
    setDraft({
      id: freshRuleId('rule'),
      name: '',
      dataPointDefinitionId: '',
      operator: 'equals',
      value: '',
    });
  }, []);

  const onRemove = useCallback(() => {
    setDraft(null);
    const updated = editNode(pathway.tenantId, pathwayId, node.id, {
      transitionRule: null,
    });
    if (updated === null) {
      setFeedback({ kind: 'error', message: 'Remove failed.' });
      return;
    }
    setFeedback({ kind: 'saved', message: 'Rule removed.' });
  }, [editNode, node.id, pathway.tenantId, pathwayId]);

  const onSave = useCallback(() => {
    if (draft === null) {
      setFeedback({ kind: 'error', message: 'Add a rule first.' });
      return;
    }
    if (draft.name.trim().length === 0) {
      setFeedback({ kind: 'error', message: 'Rule name is required.' });
      return;
    }
    if (draft.dataPointDefinitionId.trim().length === 0) {
      setFeedback({
        kind: 'error',
        message: 'Data point definition id is required.',
      });
      return;
    }
    if (
      operatorTakesValue(draft.operator) &&
      (draft.value === undefined ||
        (typeof draft.value === 'string' && draft.value.trim().length === 0) ||
        (Array.isArray(draft.value) && draft.value.length === 0))
    ) {
      setFeedback({ kind: 'error', message: 'Value is required.' });
      return;
    }
    const updated = editNode(pathway.tenantId, pathwayId, node.id, {
      transitionRule: { ...draft },
    });
    if (updated === null) {
      setFeedback({ kind: 'error', message: 'Save failed.' });
      return;
    }
    setFeedback({ kind: 'saved', message: 'Rule saved.' });
  }, [draft, editNode, node.id, pathway.tenantId, pathwayId]);

  return (
    <section
      data-testid={`pathway-inspector-transition-rule-${node.id}`}
      aria-labelledby={`pathway-inspector-transition-rule-title-${node.id}`}
      className="flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm"
    >
      <header className="flex items-baseline justify-between gap-gutter-sm">
        <h4
          id={`pathway-inspector-transition-rule-title-${node.id}`}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Transition rule
        </h4>
        {draft === null ? (
          <button
            type="button"
            data-testid={`pathway-inspector-transition-rule-add-${node.id}`}
            aria-label="Add transition rule"
            onClick={onAdd}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            Add rule
          </button>
        ) : (
          <button
            type="button"
            data-testid={`pathway-inspector-transition-rule-remove-${node.id}`}
            aria-label="Remove transition rule"
            onClick={onRemove}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            Remove rule
          </button>
        )}
      </header>
      {draft === null ? (
        <p
          data-testid={`pathway-inspector-transition-rule-empty-${node.id}`}
          className="text-signal-xs text-text-tertiary"
        >
          No rule yet. Click Add rule to gate this step's transition.
        </p>
      ) : (
        <>
          <RuleControls
            testidPrefix={`pathway-inspector-transition-rule-${node.id}`}
            rule={draft}
            onChange={setDraft}
          />
          <div className="flex items-center justify-between gap-gutter-sm">
            <button
              type="button"
              data-testid={`pathway-inspector-transition-rule-save-${node.id}`}
              onClick={onSave}
              className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-600 focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              Save rule
            </button>
            {feedback.kind !== 'idle' ? (
              <span
                role={feedback.kind === 'error' ? 'alert' : 'status'}
                data-testid={`pathway-inspector-transition-rule-feedback-${node.id}`}
                data-feedback-kind={feedback.kind}
                className={[
                  'text-signal-xs',
                  feedback.kind === 'error'
                    ? 'text-signal-danger-500'
                    : 'text-signal-success-500',
                ].join(' ')}
              >
                {feedback.message}
              </span>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}

interface FormQuestionEnableWhenEditorProps {
  nodeId: string;
  question: FormQuestion;
  onUpdate: (patch: Partial<FormQuestion>) => void;
}

function FormQuestionEnableWhenEditor({
  nodeId,
  question,
  onUpdate,
}: FormQuestionEnableWhenEditorProps) {
  const draft = question.enableWhenRule ?? null;
  const onAdd = useCallback(() => {
    onUpdate({
      enableWhenRule: {
        id: freshRuleId('enable'),
        name: '',
        dataPointDefinitionId: '',
        operator: 'equals',
        value: '',
      },
    });
  }, [onUpdate]);
  const onRemove = useCallback(() => {
    onUpdate({ enableWhenRule: undefined });
  }, [onUpdate]);
  return (
    <div
      data-testid={`pathway-inspector-form-question-rule-${nodeId}-${question.id}`}
      className="flex flex-col gap-1 rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm"
    >
      <div className="flex items-baseline justify-between gap-gutter-sm">
        <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Show this question only if
        </span>
        {draft === null ? (
          <button
            type="button"
            data-testid={`pathway-inspector-form-question-rule-add-${nodeId}-${question.id}`}
            aria-label="Add display rule"
            onClick={onAdd}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            Add rule
          </button>
        ) : (
          <button
            type="button"
            data-testid={`pathway-inspector-form-question-rule-remove-${nodeId}-${question.id}`}
            aria-label="Remove display rule"
            onClick={onRemove}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            Remove rule
          </button>
        )}
      </div>
      {draft === null ? (
        <p
          data-testid={`pathway-inspector-form-question-rule-empty-${nodeId}-${question.id}`}
          className="text-signal-xs text-text-tertiary"
        >
          Always shown. Click Add rule to make this question conditional.
        </p>
      ) : (
        <RuleControls
          testidPrefix={`pathway-inspector-form-question-rule-${nodeId}-${question.id}`}
          rule={draft}
          onChange={(next) => onUpdate({ enableWhenRule: next })}
        />
      )}
    </div>
  );
}

export { EMPTY_STATE_MESSAGE, TSH_HC8_HINT, ETAG_CONFLICT_MESSAGE };
